import { http } from '@/lib/axios';
import { unwrap, toApiError, getPage } from '@/lib/unwrap';
import type { ApiEnvelope, SearchParams } from '@/types/api';
import type {
  CreateSalePayload,
  CreateSaleResult,
  SaleDetail,
  SaleLookupRow,
  Shift,
  ShiftSummary,
  SyncResult,
  ResumePaymentResult,
} from '@/types/sale';

export async function createSale(
  payload: CreateSalePayload,
  idempotencyKey?: string,
): Promise<CreateSaleResult> {
  try {
    // A stable Idempotency-Key makes a flaky-network retry safe — the server
    // returns the original sale instead of creating a second one.
    const res = await http.post<ApiEnvelope<CreateSaleResult>>('/sale', payload, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Authoritative dry-run cart total. Same body as createSale; no write/reservation. */
export interface SaleQuote {
  total: number;
  totalDiscount: number;
}

export async function getSaleQuote(payload: CreateSalePayload): Promise<SaleQuote> {
  try {
    const res = await http.post<ApiEnvelope<SaleQuote>>('/sale/quote', payload);
    const data = unwrap(res.data) as Partial<SaleQuote>;
    return {
      total: typeof data.total === 'number' ? data.total : 0,
      totalDiscount: typeof data.totalDiscount === 'number' ? data.totalDiscount : 0,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSaleDetail(invoiceId: string): Promise<SaleDetail> {
  try {
    const res = await http.post<ApiEnvelope<{ sale?: SaleDetail }>>(
      `/sale/detail/${invoiceId}`,
      {},
    );
    // The endpoint returns { result, page, sale } — the flat receipt (invoice
    // header + items + tickets) lives on `sale`. Fall back to the raw payload
    // for resilience if an older backend omits it.
    const data = unwrap(res.data) as { sale?: SaleDetail } & Partial<SaleDetail>;
    return (data?.sale ?? (data as SaleDetail));
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Re-open an unpaid sale so the cashier can finish taking payment.
 *
 * The server asks the gateway BEFORE answering, so a sale the customer already
 * paid — whose webhook never arrived — comes back SUCCESS rather than as a
 * code to re-show. Refuses 422 for tenders that cannot be continued this way
 * (e-money, EDC, cash); the message names which.
 */
export async function resumePayment(invoiceId: string): Promise<ResumePaymentResult> {
  try {
    const res = await http.post<ApiEnvelope<ResumePaymentResult>>(
      `/sale/resume/${invoiceId}`,
      {},
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function syncMidtrans(invoiceId: string): Promise<SyncResult> {
  try {
    const res = await http.post<ApiEnvelope<SyncResult>>(`/sale/sync/midtrans/${invoiceId}`, {});
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function syncCashless(invoiceId: string): Promise<SyncResult> {
  try {
    const res = await http.post<ApiEnvelope<SyncResult>>(`/sale/sync/cashless/${invoiceId}`, {});
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export interface SaleLookupResult {
  rows: SaleLookupRow[];
  total: number;
  pageNumber: number;
  perPage: number;
  totalPage: number;
}

/**
 * GET /sale/lookup — browse/search sales. `q` is optional (blank = all sales
 * for the date); `date` scopes to one Asia/Jakarta day; paginated. Returns
 * { data, page }; tolerates the legacy bare-array shape.
 */
export async function lookupSales(params: {
  q?: string;
  date?: string;
  page?: number;
  perPage?: number;
  /** 'own' (caller's sales) or 'all' (every cashier's). Defaults to 'own'. */
  scope?: 'own' | 'all';
}): Promise<SaleLookupResult> {
  try {
    const res = await http.get<ApiEnvelope<SaleLookupRow[]>>('/sale/lookup', {
      params: {
        q: params.q?.trim() || undefined,
        date: params.date || undefined,
        page_number: params.page ?? 1,
        per_page: params.perPage ?? 25,
        scope: params.scope ?? 'own',
      },
    });
    // unwrap() returns the `data` array and stashes the envelope's top-level
    // `page` on a symbol (getPage reads it).
    const rows = unwrap(res.data);
    const list = Array.isArray(rows) ? rows : [];
    const page = getPage(rows);
    return {
      rows: list,
      total: page?.total ?? list.length,
      pageNumber: page?.page_number ?? 1,
      perPage: page?.per_page ?? params.perPage ?? 25,
      totalPage: page?.total_page ?? 1,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSaleList(params?: SearchParams): Promise<SaleLookupRow[]> {
  try {
    const res = await http.get<ApiEnvelope<SaleLookupRow[]>>('/sale', { params });
    const data = unwrap(res.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * GET /sale/shift-summary?date= — defensive fail-open: the endpoint may not be
 * live yet, in which case the screen renders a graceful placeholder.
 */
export async function getShiftSummary(date: string): Promise<ShiftSummary | null> {
  try {
    const res = await http.get<ApiEnvelope<ShiftSummary>>('/sale/shift-summary', {
      params: { date },
    });
    return unwrap(res.data);
  } catch (error) {
    const err = toApiError(error);
    // 404 / not-implemented → graceful placeholder upstream.
    if (err.code === 404 || err.code === 501) return null;
    throw err;
  }
}

// ── Till sessions ──────────────────────────────────────────────────────────

/** The cashier's currently-open shift, or null when none is open. */
export async function getCurrentShift(): Promise<Shift | null> {
  try {
    const res = await http.get<ApiEnvelope<Shift | null>>('/sale/shift/current');
    return unwrap(res.data);
  } catch (error) {
    const err = toApiError(error);
    if (err.code === 404 || err.code === 501) return null;
    throw err;
  }
}

/** Open a till session with an optional opening cash float. */
export async function openShift(openingFloat = 0): Promise<Shift> {
  try {
    const res = await http.post<ApiEnvelope<Shift>>('/sale/shift/open', { openingFloat });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Close the open shift with the counted drawer; returns the reconciled shift. */
export async function closeShift(input: {
  countedCash: number;
  denominations?: Record<string, number> | null;
  note?: string | null;
}): Promise<Shift> {
  try {
    const res = await http.post<ApiEnvelope<Shift>>('/sale/shift/close', input);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
